import { MAX_EDIT_AREA_TEMPLATE_NAME_LENGTH } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { Button, Input } from '@/shared/ui';
import { useCreateTemplateFromProduct } from '../model/use-create-template-from-product';
import { CategorySelect } from './category-select';
import { ProductSearchList } from './product-search-list';

interface TemplateFromProductModalProps {
  /** 상품 편집영역 화면에서 열면 그 상품이 미리 골라진다. */
  readonly initialProduct?: { readonly id: string; readonly name: string };
  readonly initialCategoryId?: string | null;
  readonly onCreated: () => void;
  readonly onClose: () => void;
}

/** "이 상품 설정으로 템플릿 만들기" — 편집영역이 저장된 상품의 기하·실측·폰트/잉크/인쇄 방식을 템플릿으로 남긴다. */
export const TemplateFromProductModal = ({
  initialProduct,
  initialCategoryId = null,
  onCreated,
  onClose,
}: TemplateFromProductModalProps): ReactElement => {
  const [product, setProduct] = useState(initialProduct ?? null);
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(initialCategoryId);
  const action = useCreateTemplateFromProduct({ onSuccess: onCreated });

  // 상품 검색 폼이 안에 있어 바깥을 form 으로 두지 않는다(중첩 form 금지) — 만들기는 버튼 클릭으로 보낸다.
  const handleSubmit = (): void => {
    if (product === null || name.trim() === '') return;
    action.createFromProduct({ productId: product.id, name: name.trim(), categoryId });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="상품 설정으로 템플릿 만들기"
        className="flex max-h-[85dvh] w-full max-w-lg flex-col gap-4 overflow-y-auto rounded-lg border bg-background p-5"
      >
        <div>
          <p className="font-medium">상품 설정으로 템플릿 만들기</p>
          <p className="mt-1 text-sm text-muted-foreground">
            편집영역이 저장된 상품의 면·표면·인쇄 영역(mm)·실측 종류와 허용 폰트·잉크·인쇄 방식을
            가져옵니다. 목업 사진과 원통 마스크는 상품마다 다르므로 담지 않습니다.
          </p>
        </div>
        {action.error ? (
          <p role="alert" className="text-sm text-destructive">
            {action.error.message}
          </p>
        ) : null}
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor="edit-area-template-name">템플릿 이름</label>
          <Input
            id="edit-area-template-name"
            value={name}
            maxLength={MAX_EDIT_AREA_TEMPLATE_NAME_LENGTH}
            placeholder="예: 반팔 티셔츠"
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1 text-sm">
          기본 템플릿으로 쓸 카테고리
          <CategorySelect label="템플릿 카테고리" value={categoryId} onChange={setCategoryId} />
        </div>
        <div className="flex flex-col gap-2 text-sm">
          <span>원본 상품{product ? ` — ${product.name}` : ''}</span>
          {initialProduct === undefined ? (
            <ProductSearchList
              actionLabel="선택"
              selectedProductId={product?.id ?? null}
              onPick={setProduct}
            />
          ) : null}
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            취소
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleSubmit}
            disabled={action.isPending || product === null || name.trim() === ''}
          >
            템플릿 만들기
          </Button>
        </div>
      </div>
    </div>
  );
};
